import React, { useState, useEffect } from 'react';
import server from '../../../utils/serverConfig';
import apiClient from '../../../utils/apiClient';
import ScreenerDetailModal from './ScreenerDetailModal';
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Brush
} from 'recharts';

const StockScreenerView = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [aggregatedData, setAggregatedData] = useState({});
    const [allData, setAllData] = useState([]);
    const [modalConfig, setModalConfig] = useState({ open: false, title: "", data: [] });
    const [niftyData, setNiftyData] = useState([]);
    
    // Default to last 30 days
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'screener'
    const [isChartFullScreen, setIsChartFullScreen] = useState(false);

    // Y-Axis Panning State
    const [yDomain, setYDomain] = useState(['auto', 'auto']);
    const [isDraggingY, setIsDraggingY] = useState(false);
    const [startY, setStartY] = useState(0);

    const handleQuickRange = (months) => {
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - months);
        
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
    };

    const handleWheelY = (e) => {
        // Only zoom Y if scrolling over the chart area (can refine to right axis later)
        e.preventDefault();
        setYDomain(prev => {
            const min = prev[0] === 'auto' ? Math.min(...niftyData.map(d => d.niftyClose)) : prev[0];
            const max = prev[1] === 'auto' ? Math.max(...niftyData.map(d => d.niftyClose)) : prev[1];
            if (!min || !max) return prev;

            const range = max - min;
            const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9; // Zoom out/in
            
            const newRange = range * zoomFactor;
            const center = (max + min) / 2;
            
            return [center - newRange / 2, center + newRange / 2];
        });
    };

    const handleMouseDownY = (e) => {
        setIsDraggingY(true);
        setStartY(e.clientY);
    };

    const handleMouseMoveY = (e) => {
        if (!isDraggingY) return;
        
        const delta = e.clientY - startY;
        setStartY(e.clientY);

        setYDomain(prev => {
            const min = prev[0] === 'auto' ? Math.min(...niftyData.map(d => d.niftyClose)) : prev[0];
            const max = prev[1] === 'auto' ? Math.max(...niftyData.map(d => d.niftyClose)) : prev[1];
            if (!min || !max) return prev;

            const range = max - min;
            const shift = (delta / 200) * range; // Sensitivity
            
            return [min + shift, max + shift];
        });
    };

    const handleMouseUpY = () => {
        setIsDraggingY(false);
    };

    const resetYDomain = () => {
        setYDomain(['auto', 'auto']);
    };

    // Indices to display in order
    const INDICES = ["NIFTY 50", "NIFTY 100", "NIFTY 200", "SMALLCAP 250"];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch screener results
                const res = await apiClient.get(`${server.godseye.baseUrl}api/screener-results?start_date=${startDate}&end_date=${endDate}`);
                const json = res.data;

                // Fetch Nifty 50 OHLCV tracking data
                let niftyJson = { data: [] };
                try {
                    const niftyRes = await apiClient.get(`${server.godseye.baseUrl}api/nifty-daily-ohlcv?start_date=${startDate}&end_date=${endDate}`);
                    niftyJson = niftyRes.data;
                } catch (e) {
                    console.warn(e);
                }

                console.log("Screener Data Fetched:", json.data); // DEBUG
                console.log("Nifty Data Fetched:", niftyJson.data); // DEBUG

                processData(json.data, niftyJson.data);
                setAllData(json.data);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    const processData = (rawData, niftyRawData) => {
        // Group by Date -> Index -> Counts
        const processed = {};

        rawData.forEach(row => {
            const date = row.date;
            if (!processed[date]) processed[date] = {};

            const idx = row.index_name.toUpperCase();
            if (!processed[date][idx]) processed[date][idx] = { hpchu: 0, lpcld: 0 };

            if (row.hpchu_signal === 1) processed[date][idx].hpchu += 1;
            if (row.lpcld_signal === 1) processed[date][idx].lpcld += 1;
        });

        // Merge Nifty 50 OHLCV data with the computed percentages
        const mergedNiftyData = [];

        // Convert nifty raw data into an easily accessible map
        const niftyMap = {};
        if (niftyRawData && Array.isArray(niftyRawData)) {
            niftyRawData.forEach(row => {
                niftyMap[row.date] = row;
            });
        }

        // Build the chart data based on continuous dates
        const allDates = Object.keys(processed).sort((a, b) => new Date(a) - new Date(b));

        if (allDates.length > 0) {
            const startDate = new Date(allDates[0]);
            const endDate = new Date(allDates[allDates.length - 1]);

            // Generate full date range
            const fullDateRange = [];
            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                fullDateRange.push(d.toISOString().split('T')[0]);
            }

            let lastValidDay = { hpchuPct: null, lpcldPct: null, niftyClose: null };

            fullDateRange.forEach(date => {
                const nifty50Data = processed[date]?.["NIFTY 50"];
                const niftyClose = niftyMap[date]?.close;

                // If data exists, use it and update last valid. If not, fallback to last valid day
                if (nifty50Data || niftyClose) {
                    const hpchuPct = nifty50Data ? (nifty50Data.hpchu / 50) * 100 : lastValidDay.hpchuPct;
                    const lpcldPct = nifty50Data ? (nifty50Data.lpcld / 50) * 100 : lastValidDay.lpcldPct;
                    const close = niftyClose || lastValidDay.niftyClose;

                    lastValidDay = { hpchuPct, lpcldPct, niftyClose: close };
                }

                mergedNiftyData.push({
                    date: new Date(date).toLocaleDateString('en-GB'),
                    rawDate: date,
                    niftyClose: lastValidDay.niftyClose,
                    hpchuPct: lastValidDay.hpchuPct,
                    lpcldPct: lastValidDay.lpcldPct,
                    isRealData: !!(nifty50Data || niftyClose) // Flag to filter table
                });
            });
        }

        setAggregatedData(processed);
        setNiftyData(mergedNiftyData);
    };

    const handleCellClick = (date, indexName, signalType) => { // signalType: 'HPCHU' | 'LPCLD'
        // Filter raw data
        const filtered = allData.filter(d =>
            d.date === date &&
            d.index_name.toUpperCase() === indexName &&
            (signalType === 'HPCHU' ? d.hpchu_signal === 1 : d.lpcld_signal === 1)
        );

        setModalConfig({
            open: true,
            title: `${indexName} - ${signalType} (${date})`,
            data: filtered
        });
    };

    const handleIndexClick = (indexName) => {
        // Filter all data for this index across ALL fetched dates
        // Show BOTH signals (or maybe separate? User said "combine modal which contains all")
        // "Show all" implies showing every stock that had EITHER signal on ANY day for this index?
        // Or just list everything found in the DB for this index?
        // Context: "Index" row header. 
        // Let's show all stocks that had VALID signals (HPCHU=1 OR LPCLD=1) for this index in the fetched range.

        const filtered = allData.filter(d =>
            d.index_name.toUpperCase() === indexName &&
            (d.hpchu_signal === 1 || d.lpcld_signal === 1)
        );

        // Sort by Date Descending
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

        setModalConfig({
            open: true,
            title: `${indexName} - All Signals (${startDate} to ${endDate})`,
            data: filtered
        });
    };

    // Get sorted unique dates (descending)
    const dates = Object.keys(aggregatedData).sort((a, b) => new Date(b) - new Date(a));

    if (loading) return <div className="p-8 text-center text-slate-400 animate-pulse">Loading Screener Data...</div>;
    if (error) return <div className="p-8 text-center text-rose-400">Error: {error}</div>;


    return (
        <>
            <ScreenerDetailModal
                isOpen={modalConfig.open}
                onClose={() => setModalConfig({ ...modalConfig, open: false })}
                title={modalConfig.title}
                data={modalConfig.data}
            />

            <div className="mt-6 flex flex-col bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-xl overflow-hidden">
                {/* Tabs Header */}
                <div className="flex border-b border-slate-700/50 bg-[#0f172a] justify-between items-center pr-4">
                    <div className="flex w-2/3">
                        <button
                            onClick={() => setActiveTab('summary')}
                            className={`flex-1 py-4 text-sm font-semibold transition-colors ${activeTab === 'summary' ? 'text-blue-400 border-b-2 border-blue-400 bg-slate-800/50' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'}`}
                        >
                            Nifty Daily Summary
                        </button>
                        <button
                            onClick={() => setActiveTab('screener')}
                            className={`flex-1 py-4 text-sm font-semibold transition-colors ${activeTab === 'screener' ? 'text-blue-400 border-b-2 border-blue-400 bg-slate-800/50' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/30'}`}
                        >
                            Detailed Data Screener
                        </button>
                    </div>

                    {/* Date Range Selector */}
                    <div className="flex items-center gap-3">
                        {/* Quick Ranges */}
                        <div className="hidden sm:flex items-center gap-1 mr-2 bg-[#0f172a] rounded-lg p-1 border border-slate-700/50">
                            {[
                                { label: '1M', value: 1 },
                                { label: '3M', value: 3 },
                                { label: '6M', value: 6 },
                                { label: '1Y', value: 12 },
                                { label: '2Y', value: 24 }
                            ].map(range => (
                                <button
                                    key={range.label}
                                    onClick={() => handleQuickRange(range.value)}
                                    className="px-2 py-1 text-[10px] font-bold text-slate-400 hover:text-slate-100 hover:bg-slate-700/50 rounded transition-colors"
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">From</span>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="bg-[#1e293b] text-slate-200 text-xs font-bold border border-slate-700/50 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all hover:border-slate-600"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">To</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="bg-[#1e293b] text-slate-200 text-xs font-bold border border-slate-700/50 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all hover:border-slate-600"
                            />
                        </div>
                    </div>
                </div>

                {/* Nifty 50 Summary Section */}
                {activeTab === 'summary' && niftyData.length > 0 && (
                    <div className="flex flex-col lg:flex-row gap-6 p-6 bg-[#0f172a]/30 justify-start items-stretch">

                        {/* Data Table Card */}
                        <div className="w-full lg:w-1/3 flex-shrink-0 rounded-xl border border-slate-700/50 flex flex-col max-h-[420px]">
                            <div className="px-5 py-4 border-b border-slate-700/50 bg-[#1e293b]/30 flex-shrink-0 relative z-20">
                                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                                    <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                    Historical Data
                                </h3>
                            </div>
                            <div className="overflow-auto custom-scrollbar relative">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-[#0f172a] text-slate-400 uppercase text-[11px] font-semibold tracking-wider sticky top-0 z-10 shadow-md border-b border-slate-700/50">
                                        <tr>
                                            <th className="py-3 px-5 border-r border-slate-700/50">Date</th>
                                            <th className="py-3 px-5 text-right border-r border-slate-700/50">Nifty</th>
                                            <th className="py-3 px-5 text-right border-r border-slate-700/50 text-emerald-400">HPCHU</th>
                                            <th className="py-3 px-5 text-right text-rose-400">LPCLD</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-700/50 bg-[#1e293b]">
                                        {/* Map reversed so latest date is on top in table. We only map rows that have real data. */}
                                        {[...niftyData].reverse().filter(row => row.isRealData).map((row, i) => (
                                            <tr key={i} className="hover:bg-slate-700/30 transition-colors">
                                                <td className="py-2.5 px-5 text-slate-300 font-mono border-r border-slate-700/50 whitespace-nowrap">{row.date}</td>
                                                <td className="py-2.5 px-5 text-slate-100 font-bold text-right border-r border-slate-700/50">{row.niftyClose ? row.niftyClose.toLocaleString('en-IN') : '-'}</td>
                                                <td className="py-2.5 px-5 text-emerald-400 text-right font-mono border-r border-slate-700/50">{row.hpchuPct > 0 ? `${row.hpchuPct.toFixed(0)}%` : '-'}</td>
                                                <td className="py-2.5 px-5 text-rose-400 text-right font-mono">{row.lpcldPct > 0 ? `${row.lpcldPct.toFixed(0)}%` : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Chart Area Card */}
                        <div className={isChartFullScreen
                            ? "fixed inset-0 z-[9999] bg-[#0f172a] flex flex-col animate-in fade-in duration-300 h-screen w-screen overflow-hidden"
                            : "w-full lg:w-2/3 flex-1 max-w-5xl bg-[#1e293b] rounded-xl border border-slate-700/50 shadow-lg flex flex-col overflow-hidden"
                        }>

                            {/* Chart Header & Controls */}
                            <div className="px-5 py-4 border-b border-slate-700/50 bg-[#1e293b]/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shrink-0">
                                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>
                                    {isChartFullScreen ? "Historical Market Trends" : "Market Trends"}
                                </h3>
                                <div className="flex items-center gap-2">
                                    {yDomain[0] !== 'auto' && (
                                        <button 
                                            onClick={resetYDomain}
                                            className="bg-[#1e293b]/80 text-[10px] uppercase font-bold text-blue-400 border border-blue-500/30 px-2 py-1 rounded hover:bg-[#2d3748] transition-colors"
                                        >
                                            Reset Y-Zoom
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setIsChartFullScreen(!isChartFullScreen)}
                                        className="bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded p-1.5 text-slate-300 hover:text-white transition-colors flex items-center justify-center h-full"
                                        title={isChartFullScreen ? "Minimize Chart" : "Maximize Chart"}
                                    >
                                        {isChartFullScreen ?
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" /></svg>
                                            :
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" /></svg>
                                        }
                                    </button>
                                </div>
                            </div>

                            {/* Dual Chart Body */}
                            <div className={`w-full ${isChartFullScreen ? 'flex-1 min-h-0' : 'h-[600px]'} p-4 pt-6 flex flex-col gap-0`}>
                                {/* Top Chart: Nifty Price */}
                                <div 
                                    className="flex-grow min-h-0 relative cursor-crosshair"
                                    onWheel={handleWheelY}
                                    onMouseDown={handleMouseDownY}
                                    onMouseMove={handleMouseMoveY}
                                    onMouseUp={handleMouseUpY}
                                    onMouseLeave={handleMouseUpY}
                                >
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart syncId="niftyBreadth" data={niftyData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                            <XAxis 
                                                dataKey="rawDate" 
                                                hide={true} 
                                            />
                                            {/* Left Placeholder for alignment - width must match bottom chart's left axis */}
                                            <YAxis yAxisId="left" orientation="left" width={60} axisLine={false} tickLine={false} tick={false} />
                                            
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                stroke="#94a3b8"
                                                fontSize={11}
                                                width={60}
                                                domain={yDomain}
                                                allowDataOverflow={true}
                                                tickFormatter={(v) => v.toLocaleString()}
                                            />
                                            <Tooltip
                                                labelFormatter={(label) => new Date(label).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                formatter={(value, name) => [value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), name]}
                                                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#475569', borderRadius: '8px' }}
                                                itemStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                                                labelStyle={{ color: '#94a3b8', marginBottom: '8px' }}
                                            />
                                            <Line yAxisId="right" type="linear" dataKey="niftyClose" name="Nifty" stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                            <Legend verticalAlign="top" align="right" height={25} wrapperStyle={{ fontSize: '11px', top: -10 }} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Bottom Chart: HPCHU & LPCLD */}
                                <div className="flex-grow min-h-0 mt-2 border-t border-slate-700/30 pt-4">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart syncId="niftyBreadth" data={niftyData} margin={{ top: 0, right: 10, left: 10, bottom: 20 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                            <XAxis 
                                                dataKey="rawDate" 
                                                stroke="#94a3b8" 
                                                fontSize={10} 
                                                tickMargin={10}
                                                minTickGap={30}
                                                tickFormatter={(tick) => {
                                                    const d = new Date(tick);
                                                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
                                                }}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                orientation="left"
                                                stroke="#94a3b8"
                                                fontSize={11}
                                                width={60}
                                                domain={[0, 100]}
                                                tickFormatter={(v) => `${v}%`}
                                            />
                                            {/* Right Placeholder for alignment - width must match top chart's right axis */}
                                            <YAxis yAxisId="right" orientation="right" width={60} axisLine={false} tickLine={false} tick={false} />

                                            <Tooltip
                                                labelFormatter={(label) => new Date(label).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                formatter={(value, name) => [`${value.toFixed(2)}%`, name]}
                                                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#475569', borderRadius: '8px' }}
                                                itemStyle={{ fontSize: '13px', fontWeight: 'bold' }}
                                                labelStyle={{ color: '#94a3b8', marginBottom: '8px' }}
                                            />
                                            <Line yAxisId="left" type="linear" dataKey="hpchuPct" name="HPCHU" stroke="#10b981" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                            <Line yAxisId="left" type="linear" dataKey="lpcldPct" name="LPCLD" stroke="#ef4444" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                                            <Legend verticalAlign="top" align="left" height={25} wrapperStyle={{ fontSize: '11px', top: -10 }} />
                                            <Brush 
                                                dataKey="rawDate" 
                                                height={30} 
                                                stroke="#3b82f6" 
                                                fill="#0f172a" 
                                                tickFormatter={() => ''} 
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'screener' && (
                    <div className={isChartFullScreen ? "flex-1 overflow-auto custom-scrollbar" : "overflow-auto custom-scrollbar"}>
                        <table className="w-full text-sm border-collapse">
                            <thead className="bg-[#0f172a] text-slate-400 uppercase font-semibold text-xs sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="py-4 px-6 text-left border-b border-r border-slate-700 bg-[#0f172a] sticky left-0 z-20">Traditional</th>
                                    {dates.map(date => (
                                        <th key={date} colSpan={2} className="py-4 px-2 text-center border-b border-r border-slate-700 min-w-[140px]">
                                            {new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                                            <div className="flex justify-around text-[10px] text-slate-500 mt-1 pt-1 border-t border-slate-700/50">
                                                <span className="text-emerald-500 w-1/2">HPCHU</span>
                                                <span className="text-rose-500 w-1/2 border-l border-slate-700/50">LPCLD</span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {INDICES.map(indexName => (
                                    <tr key={indexName} className="hover:bg-slate-700/20 transition-colors">
                                        <td
                                            onClick={() => handleIndexClick(indexName)}
                                            className="py-4 px-6 font-bold text-slate-200 border-r border-slate-700 bg-[#1e293b] sticky left-0 z-10 whitespace-nowrap cursor-pointer hover:text-indigo-400 hover:bg-[#2d3748] transition-colors"
                                        >
                                            {indexName}
                                        </td>
                                        {dates.map(date => {
                                            const cellData = aggregatedData[date]?.[indexName] || { hpchu: 0, lpcld: 0 };
                                            return (
                                                <React.Fragment key={`${indexName}-${date}`}>
                                                    <td
                                                        onClick={() => handleCellClick(date, indexName, 'HPCHU')}
                                                        className={`py-3 px-2 text-center font-mono cursor-pointer transition-colors border-r border-slate-700/30 w-[70px] ${cellData.hpchu > 0 ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 font-bold' : 'text-slate-600'}`}
                                                    >
                                                        {cellData.hpchu || '-'}
                                                    </td>
                                                    <td
                                                        onClick={() => handleCellClick(date, indexName, 'LPCLD')}
                                                        className={`py-3 px-2 text-center font-mono cursor-pointer transition-colors border-r border-slate-700 w-[70px] ${cellData.lpcld > 0 ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 font-bold' : 'text-slate-600'}`}
                                                    >
                                                        {cellData.lpcld || '-'}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default StockScreenerView;
